import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Simple in-memory cache
const vehicleCache = {
  data: null,
  timestamp: null,
  CACHE_DURATION: 5 * 60 * 1000, // 5 minutes
};

export const useVehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchVehicles = useCallback(async (forceRefresh = false) => {
    // Check cache first
    const now = Date.now();
    if (
      !forceRefresh &&
      vehicleCache.data &&
      vehicleCache.timestamp &&
      now - vehicleCache.timestamp < vehicleCache.CACHE_DURATION
    ) {
      setVehicles(vehicleCache.data);
      setLoading(false);
      return vehicleCache.data;
    }

    setLoading(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const response = await axios.get(`${API}/vehicles/`);
      
      // Update cache
      vehicleCache.data = response.data;
      vehicleCache.timestamp = now;
      
      setVehicles(response.data);
      setError(null);
      return response.data;
    } catch (err) {
      console.error('Error fetching vehicles:', err);
      setError(err);
      // Return cached data if available even on error
      if (vehicleCache.data) {
        setVehicles(vehicleCache.data);
      }
      return vehicleCache.data || [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  const invalidateCache = useCallback(() => {
    vehicleCache.data = null;
    vehicleCache.timestamp = null;
  }, []);

  return { vehicles, loading, error, refetch: fetchVehicles, invalidateCache };
};

export const useVehicleById = (id) => {
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVehicle = async () => {
      // Check cache first
      if (vehicleCache.data) {
        const cachedVehicle = vehicleCache.data.find(v => v.id === id);
        if (cachedVehicle) {
          setVehicle(cachedVehicle);
          setLoading(false);
          return;
        }
      }

      // Fetch from API if not in cache
      setLoading(true);
      try {
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        const response = await axios.get(`${API}/vehicles/${id}`);
        setVehicle(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching vehicle:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchVehicle();
  }, [id]);

  return { vehicle, loading, error };
};
